/**
 * Invariant enforcement + structural edit operations, all pure (operate on
 * already-cloned data — the timeline store clones before mutating).
 *
 * Format invariants maintained here:
 *  - clips sorted by start
 *  - no overlap without a transition; overlaps only on video tracks
 *  - transitionIn.duration == overlap, and <= min duration of both clips
 *  - transitionIn removed when there is no overlap
 */
import { clipDuration, clipEnd, genId, reassignEffectIds } from './helpers';
import type { AnyClip, Edit, Track, TransitionType, VideoClip, VideoTrack } from './types';

export const MIN_CLIP_DURATION = 0.05;

function sortClips(clips: AnyClip[]): void {
  clips.sort((a, b) => a.start - b.start || a.id.localeCompare(b.id));
}

/**
 * Normalize one track in place after any mutation.
 * Video tracks: overlaps become/stay transitions (auto-crossfade), clamped to
 * the shorter participating clip; stale transitions are dropped.
 * Other tracks: overlaps are resolved by pushing the later clip right.
 */
export function normalizeTrack(track: Track): void {
  sortClips(track.clips as AnyClip[]);
  if (track.kind === 'video') {
    normalizeVideoTrack(track);
    return;
  }
  const clips = track.clips as AnyClip[];
  for (let i = 1; i < clips.length; i++) {
    const prevEnd = clipEnd(clips[i - 1]);
    if (clips[i].start < prevEnd - 1e-9) clips[i].start = prevEnd;
  }
}

function normalizeVideoTrack(track: VideoTrack): void {
  const clips = track.clips;
  if (clips.length > 0) clips[0].transitionIn = null; // first clip: nothing to transition from
  for (let i = 1; i < clips.length; i++) {
    const a = clips[i - 1];
    const b = clips[i];
    const aEnd = clipEnd(a);
    let overlap = aEnd - b.start;
    if (overlap <= 1e-6) {
      // no overlap: a transition would be meaningless
      b.transitionIn = null;
      continue;
    }
    const maxOverlap = Math.max(
      0,
      Math.min(clipDuration(a), clipDuration(b)) - 1e-3,
    );
    if (overlap > maxOverlap) {
      // clamp: shift b right so the overlap fits inside both clips
      b.start = aEnd - maxOverlap;
      overlap = maxOverlap;
    }
    if (overlap <= 1e-6) {
      b.start = aEnd;
      b.transitionIn = null;
      continue;
    }
    if (b.transitionIn) {
      b.transitionIn = { ...b.transitionIn, duration: round(overlap) };
    } else {
      b.transitionIn = { type: 'crossfade', duration: round(overlap) };
    }
  }
}

export function normalizeAllTracks(tracks: Track[]): void {
  for (const t of tracks) normalizeTrack(t);
}

/**
 * Move a clip to another track of the SAME kind, keeping its timeline
 * position. Returns false (and leaves the edit untouched) when the target is
 * missing, kind-incompatible, or the clip's current track. Video transitions
 * are dropped on move (the neighbour pair that defined them is gone); callers
 * run normalizeAllTracks afterwards, which resolves any new overlap.
 */
export function moveClipToTrack(edit: Edit, clipId: string, targetTrackId: string): boolean {
  for (const track of edit.tracks) {
    const clips = track.clips as AnyClip[];
    const idx = clips.findIndex((c) => c.id === clipId);
    if (idx < 0) continue;
    if (track.id === targetTrackId) return false;
    const target = edit.tracks.find((t) => t.id === targetTrackId);
    if (!target || target.kind !== track.kind) return false;
    const [clip] = clips.splice(idx, 1);
    if (track.kind === 'video') {
      (clip as VideoClip).transitionIn = null;
      const next = clips[idx] as VideoClip | undefined;
      if (next?.transitionIn && idx === 0) next.transitionIn = null;
    }
    (target.clips as AnyClip[]).push(clip as never);
    return true;
  }
  return false;
}

/**
 * Remove a transition per format semantics: shift the clip right so it abuts
 * the previous clip (removes the overlap), drop transitionIn.
 */
export function removeTransition(track: VideoTrack, clipId: string): void {
  const i = track.clips.findIndex((c) => c.id === clipId);
  if (i <= 0) return;
  const b = track.clips[i];
  const aEnd = clipEnd(track.clips[i - 1]);
  if (b.transitionIn && b.start < aEnd) b.start = aEnd;
  b.transitionIn = null;
}

/**
 * Set/adjust a transition on clip B by growing/shrinking the overlap with the
 * previous clip (moves B.start; duration is clamped to both clip durations).
 */
export function setTransition(
  track: VideoTrack,
  clipId: string,
  type: TransitionType,
  duration: number,
): void {
  const i = track.clips.findIndex((c) => c.id === clipId);
  if (i <= 0) return;
  const a = track.clips[i - 1];
  const b = track.clips[i];
  const aEnd = clipEnd(a);
  const d = Math.max(
    0.05,
    Math.min(duration, clipDuration(a) - 1e-3, clipDuration(b) - 1e-3),
  );
  b.start = aEnd - d;
  b.transitionIn = { type, duration: round(d) };
  normalizeTrack(track);
}

/**
 * Split a video clip at timeline time t. Returns the new (right) clip id, or
 * null if t is not strictly inside the clip.
 */
export function splitVideoClip(track: VideoTrack, clipId: string, t: number): string | null {
  const i = track.clips.findIndex((c) => c.id === clipId);
  if (i < 0) return null;
  const c = track.clips[i];
  const end = clipEnd(c);
  if (t <= c.start + MIN_CLIP_DURATION || t >= end - MIN_CLIP_DURATION) return null;
  const srcT = c.in + (t - c.start) * c.speed;
  const right: VideoClip = {
    ...c,
    id: genId('c'),
    in: srcT,
    start: t,
    fadeIn: 0,
    transitionIn: null,
    // deep-clone: animated params carry keyframe arrays that must not be
    // shared between the two halves (keyframes are source-time, so no
    // re-timing is needed — both halves keep the same source mapping).
    // JSON clone, NOT structuredClone: callers pass immer drafts (proxies),
    // which structuredClone rejects. Fresh effect ids: never shared across clips.
    effects: reassignEffectIds(jsonClone(c.effects)),
    transform: jsonClone(c.transform),
    fadeOut: c.fadeOut,
  };
  const left: VideoClip = { ...c, out: srcT, fadeOut: 0 };
  track.clips.splice(i, 1, left, right);
  return right.id;
}

/** Split an audio clip at timeline time t. */
export function splitAudioClip(
  track: Extract<Track, { kind: 'audio' }>,
  clipId: string,
  t: number,
): string | null {
  const i = track.clips.findIndex((c) => c.id === clipId);
  if (i < 0) return null;
  const c = track.clips[i];
  const end = clipEnd(c);
  if (t <= c.start + MIN_CLIP_DURATION || t >= end - MIN_CLIP_DURATION) return null;
  const srcT = c.in + (t - c.start) * (c.speed ?? 1);
  const right = { ...c, id: genId('a'), in: srcT, start: t, fadeIn: 0 };
  const left = { ...c, out: srcT, fadeOut: 0 };
  track.clips.splice(i, 1, left, right);
  return right.id;
}

/**
 * Ripple: shift every clip on `track` whose start is at/after `fromTime` by
 * `delta` seconds (in place; caller runs inside a produce/apply draft). Results
 * are clamped to start >= 0 and the track is re-sorted.
 *
 * Transition-awareness: normalizeVideoTrack turns ANY overlap into a crossfade,
 * so to close a gap after a ripple-delete callers must shift by EXACTLY the
 * freed duration — enough that neighbours abut (overlap ≈ 0) but do not overlap.
 * Over-shifting into overlap would silently manufacture a transition. This
 * helper does not clamp against neighbours; the caller owns picking `delta`.
 */
export function rippleLaterClips(track: Track, fromTime: number, delta: number): void {
  const clips = track.clips as AnyClip[];
  for (const c of clips) {
    if (c.start >= fromTime - 1e-6) {
      c.start = Math.max(0, c.start + delta);
    }
  }
  sortClips(clips);
}

/**
 * Is `[start, end)` free of clips on this track?
 *
 * Callers that place a clip at an exact time need this BEFORE inserting:
 * `normalizeTrack` resolves any overlap on a non-video track by shoving the
 * later clip right, so an unchecked insert silently retimes the result rather
 * than failing.
 */
export function trackHasRoom(track: Track, start: number, end: number): boolean {
  for (const c of track.clips as AnyClip[]) {
    if (c.start < end - 1e-6 && clipEnd(c) > start + 1e-6) return false;
  }
  return true;
}

/**
 * Snap a candidate time to the closest target within threshold (seconds).
 * Returns the snapped value (or the candidate unchanged).
 */
export function snapTime(candidate: number, targets: number[], threshold: number): number {
  let best = candidate;
  let bestDist = threshold;
  for (const target of targets) {
    const d = Math.abs(candidate - target);
    if (d < bestDist) {
      bestDist = d;
      best = target;
    }
  }
  return best;
}

/** All snap targets on a timeline: clip edges (excluding given ids), playhead, 0. */
export function snapTargets(tracks: Track[], playhead: number, excludeIds: string[]): number[] {
  const targets = [0, playhead];
  for (const track of tracks) {
    for (const clip of track.clips as AnyClip[]) {
      if (excludeIds.includes(clip.id)) continue;
      targets.push(clip.start, clipEnd(clip));
    }
  }
  return targets;
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Deep clone for pure-JSON values; unlike structuredClone it reads through
 *  immer draft proxies (the store calls these helpers on drafts). */
function jsonClone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}
